import { MicrophoneIcon } from "@heroicons/react/outline";
import { useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction } from "redux-saga";
import { ThunkDispatch } from "redux-thunk";

import {
  setDictationKey,
  setDictationText,
  toggleDictation,
} from "shared/actions/sysActions";
import { addToast, nextId } from "shared/actions/toastActions";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { findDictationKey, isSuppressedString } from "shared/utils/dictation";

import ApplicationState from "store/ApplicationState";

import getWorker from "./worker-factory";
import Spinner from "../Spinner";

import "./DictationOverlay.css";

const WHISPER_SAMPLING_RATE = 16_000;
const MAX_AUDIO_LENGTH = 120; // seconds
const MAX_SAMPLES = WHISPER_SAMPLING_RATE * MAX_AUDIO_LENGTH;
const browserSupported =
  !!navigator.mediaDevices.getUserMedia && !!(navigator as any).gpu;

const DictationOverlay = () => {
  const dispatch = useDispatch() as ThunkDispatch<any, any, AnyAction>;
  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );
  const dictationKey = useSelector(({ sys }) => sys.dictation?.key);

  const prevDictationStatus = useRef<string>("loading");
  const prevText = useRef<string>("");
  const worker = useRef<SharedWorker | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  // Dictation status: loading, loaded, warming, ready, recording, finished
  const [dictationStatus, setDictationStatus] = useState<string>("loading");
  const [text, setText] = useState("");
  const [displayText, setDisplayText] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [chunks, setChunks] = useState<any>([]);
  const [open, setOpen] = useState(false);

  const showErrorMessage = () => {
    const toastId = nextId();
    dispatch(
      addToast(
        {
          kind: "danger",
          message: "Browser does not support speech recognition",
          removalTimeout: 2000,
        },
        toastId
      )
    );
  };

  // Create a callback function for messages from the worker thread.
  const onMessageReceived = (e: any) => {
    switch (e.data.status) {
      case "loading":
        // Model file start load: add a new progress item to the list.
        setDictationStatus("loading");
        break;

      case "loaded":
        // Pipeline ready: the worker is ready to warm the model.
        setDictationStatus("loaded");
        break;

      case "warming":
        // Pipeline ready: the worker is ready to warm the model.
        setDictationStatus("warming");
        break;

      case "ready":
        // Model warmed: the worker is ready to accept messages.
        setDictationStatus("ready");
        break;

      case "start":
        // Start generation
        setIsProcessing(true);

        // Request new data from the recorder if present.
        if (recorderRef.current?.state !== "inactive") {
          recorderRef.current?.requestData();
        }
        break;

      case "complete":
        setIsProcessing(false);

        if (recorderRef.current?.state === "inactive") {
          setText("");
          setDisplayText("");
        } else {
          setText(e.data.result.text);
        }
        break;

      case "finished":
        setIsProcessing(false);
        setDictationStatus("finished");
        break;
    }
  };

  // setup the worker as soon as the component is mounted.
  useEffect(() => {
    if (browserSupported && features?.dictation) {
      if (!worker.current) {
        // Create the worker if it does not yet exist.
        worker.current = getWorker();
      }

      worker.current.port.onmessage = (event) => {
        onMessageReceived(event);
      };

      // Load the data model when the component is mounted.
      setTimeout(() => {
        worker.current?.port.postMessage({ type: "load" });
      }, 1000);

      // Define a cleanup function for when the component is unmounted.
      return () => {
        worker.current?.port.postMessage({ type: "finish" });
        worker.current = null;

        dispatch(setDictationKey(null));
        dispatch(setDictationText(""));
      };
    }
  }, []);

  const sliceAfterString = (
    stringToSlice: string,
    searchString: string
  ): string => {
    const index = stringToSlice.toLowerCase().indexOf(searchString);

    // If we can't find the search string, return the original string.
    if (index === -1) {
      return stringToSlice;
    }

    // Slice the string after the search string.
    return stringToSlice
      .slice(index + searchString.length)
      .replace(/^[\s\p{P}]+/u, "");
  };

  // Update the display text when the text changes.
  // This will slice the dispplay text based on the dictation key.
  useEffect(() => {
    if (text && text.length > 0) {
      if (text !== prevText.current) {
        prevText.current = text;

        if (!isSuppressedString(text)) {
          const foundDictationKey = findDictationKey(text);

          if (foundDictationKey && !dictationKey) {
            dispatch(setDictationKey(foundDictationKey));
          } else if (dictationKey) {
            const trimmedText = sliceAfterString(text, dictationKey);

            dispatch(setDictationText(trimmedText));
            setDisplayText(trimmedText);
          }
        }
      }
    } else {
      setDisplayText("");
    }
  }, [text]);

  useEffect(() => {
    // When the dictation model finishes warming, start recording
    // If the user opens dictation before loading is finished, warm the
    // model once loading is complete.
    if (
      open &&
      prevDictationStatus.current === "warming" &&
      dictationStatus === "ready"
    ) {
      record();
    } else if (
      open &&
      prevDictationStatus.current === "loading" &&
      dictationStatus === "loaded"
    ) {
      worker.current?.port.postMessage({ type: "warm" });
    }

    prevDictationStatus.current = dictationStatus;
  }, [dictationStatus]);

  const record = () => {
    setDictationStatus("recording");

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        recorderRef.current = new MediaRecorder(stream);
        audioContextRef.current = new AudioContext({
          sampleRate: WHISPER_SAMPLING_RATE,
        });

        recorderRef.current.onstart = () => {
          setChunks([]);
        };

        recorderRef.current.ondataavailable = (e) => {
          if (e.data.size > 0) {
            setChunks((prev: any[]) => [...prev, e.data]);
          } else {
            // Empty chunk received, so we request new data after a short timeout
            setTimeout(() => {
              if (recorderRef.current?.state !== "inactive") {
                recorderRef.current?.requestData();
              }
            }, 25);
          }
        };

        recorderRef.current.onstop = () => {
          const tracks = stream.getTracks();
          // When all tracks have been stopped the stream will
          // no longer be active and release any permissioned input
          tracks.forEach((track) => track.stop());
        };

        recorderRef.current.start();
      })
      .catch((err) => console.error("The following error occurred: ", err));
  };

  // Cleanup the recorder worker on unmount
  useEffect(() => {
    return () => {
      recorderRef.current?.stop();
      recorderRef.current = null;
    };
  }, []);

  // Generate audio and post to the worker
  useEffect(() => {
    if (!recorderRef.current) return;
    if (recorderRef.current?.state === "inactive") return;
    if (isProcessing) return;
    if (dictationStatus !== "recording") return;

    if (chunks.length > 0) {
      // Generate from data
      const blob = new Blob(chunks, { type: recorderRef.current.mimeType });

      const fileReader = new FileReader();

      fileReader.onloadend = async () => {
        const arrayBuffer = fileReader.result as ArrayBuffer;
        const decoded = await audioContextRef.current?.decodeAudioData(
          arrayBuffer
        );
        let audio = decoded && decoded.getChannelData(0);
        if (audio && audio.length > MAX_SAMPLES) {
          // Get last MAX_SAMPLES
          audio = audio.slice(-MAX_SAMPLES);
        }

        worker.current?.port.postMessage({
          type: "generate",
          data: { audio, language: "en" },
        });
      };
      fileReader.readAsArrayBuffer(blob);
    } else {
      recorderRef.current?.requestData();
    }
  }, [dictationStatus, isProcessing, chunks]);

  const resetDictation = () => {
    setOpen(false);
    setText("");
    setDisplayText("");

    worker.current?.port.postMessage({ type: "finish" });
  };

  // Toggle dictation on/off by pressing "l".
  // Dictation will submit the text when the user presses "l" again.
  useHotkeys(
    "l",
    () => {
      if (!browserSupported) {
        showErrorMessage();
      } else if (features?.dictation) {
        if (dictationStatus === "recording" || dictationStatus === "warming") {
          // Stop the recorder
          recorderRef.current?.stop();

          // Tell redux dictation is off
          dispatch(toggleDictation(false));

          resetDictation();
        } else {
          // Reset dictation data in redux when opening dictation
          dispatch(toggleDictation(true));
          dispatch(setDictationKey(null));
          dispatch(setDictationText(""));

          // Open dictation overlay
          setOpen(true);

          // If model is loaded, warm the model. If dictation is finished from
          // previous usage, load the model.
          if (dictationStatus === "loaded") {
            worker.current?.port.postMessage({ type: "warm" });
          } else if (dictationStatus === "finished") {
            // Dictation waas finished, and the worker was closed.
            // Use the shared worker and add a message listener again.
            // Send the load message, but the worker will skip loading
            // the model since it is already loaded, and post a loaded event.
            worker.current = getWorker();
            worker.current.port.onmessage = (event) => {
              onMessageReceived(event);
            };
            worker.current?.port.postMessage({ type: "load" });
          }
        }
      }
    },
    [dictationStatus]
  );

  // Pressing esc will stop dictation and reset the submitted text
  useHotkeys(
    "Escape",
    () => {
      if (dictationStatus === "recording") {
        // Stop the recorder
        recorderRef.current?.stop();

        resetDictation();
        dispatch(setDictationKey(null));
        dispatch(setDictationText(""));

        // Tell redux dictation is off
        setTimeout(() => {
          dispatch(toggleDictation(false));
        }, 10);
      } else if (
        dictationStatus === "loading" ||
        dictationStatus === "warming" ||
        dictationStatus === "ready"
      ) {
        resetDictation();
        dispatch(toggleDictation(false));
      }
    },
    [dictationStatus]
  );

  if (open === false) {
    return null;
  }

  return (
    <div className="dictation-overlay__container">
      <div className="dictation-overlay__wrapper">
        {dictationKey && (
          <span className="dictation-overlay__badge">{dictationKey}</span>
        )}
        <div className="dictation-overlay__content">
          <div className="dictation-overlay__mic">
            {dictationStatus === "loading" ? (
              <Spinner
                className="dictation-overlay__spinner"
                size="24px"
                frontColor="white"
                backColor="rgba(255, 255, 255, 0.15)"
              />
            ) : (
              <MicrophoneIcon />
            )}
          </div>
          {displayText && (
            <span className="dictation-overlay__text">{displayText}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DictationOverlay;
